import type { ThemeOptions } from '@mui/material/styles';
import type { SemanticConfig } from '../types';
import { darkPrimitives } from '../primitives';
import { buildSemanticPalette } from '../semantic';
import {
    breakpoints,
    getTypographyOptions,
    getComponentOverrides,
    DASHBOARD_FONT,
} from '../shared';

const resolved = darkPrimitives;

const semanticConfig: SemanticConfig = {
    neutral: resolved.woodsmoke,
    primary: resolved.blueribbon,
    success: resolved.shamrock,
    error: resolved.thunderbird,
    warning: resolved.sun,
    accent: resolved.riptide,
};

const semantic = buildSemanticPalette(semanticConfig);

export const darkThemeOptions: ThemeOptions = {
    palette: {
        mode: 'dark',

        // --- Legacy palette (unchanged from original theme.ts) ---
        primary: {
            main: '#247FFF',
            light: '#579DFF',
            dark: '#0B4EAC',
        },
        secondary: {
            main: '#FFAB00',
            dark: '#db8a11',
        },
        background: {
            default: '#1A1B23',
        },
        text: {
            primary: '#fff',
            secondary: '#727687',
        },
        error: {
            main: '#FD6E4E',
            light: '#ff7575',
            dark: '#4B2016',
        },
        warning: {
            main: '#FFAB00',
            light: '#FFAB001A',
        },
        info: {
            main: '#FFF',
        },
        success: {
            main: '#38CB89',
            light: '#77d0aa',
            dark: '#255744',
        },
        hypertracker: {
            main: '#15C8B1',
            light: '#8CF7E0',
            dark: '#12AA96',
        },
        based: {
            main: '#FF6940',
            light: '#FF7650',
        },
        divider: '#3B3C46',
        hyperLiquidColor: '#15C8B1',
        hyperLiquidColor2: '#8CF7E0',
        hyperLiquidColor3: '#9BE5C4',
        blueTelegram: '#29ABEE',
        backgrounds: {
            bg1: '#1A1B23',
            bg2: '#1D1E25',
            bg3: '#26272C',
            bg4: '#3B3C46',
            bg5: '#262731',
            bg6: '#F5F7FB',
            buttonSelectBg: 'rgba(140, 247, 224, 0.19)',
            sideBarBackground: '#0E0E0E',
            layoutHeaderBg: '#000000',
            pageHeaderBackground: '#1A1B23',
            progressDefaultBg: '#3B3C46',
            loginFormBg: '#26272C',
            loginInputsBg: '#3B3C46',
            tableHeadBg: '#26272C',
            tableRowBg: '#3B3C46',
            tableFooterBg: '#3B3C46',
            successDark100: '#2F8C64',
            errorDark100: '#743D33',
            errorDark50: '#BE5841',
            modalBodyBg: '#3B3C46',
            modalBlurredBg: '#1A1B230A',
            bullMeterVeryBullishBg: '#1F3D37',
            bullMeterBullishBg: '#2F5950',
            bullMeterSlightlyBullishBg: '#386C62',
            bullMeterVeryBearishBg: '#5E3430',
            bullMeterBearishBg: '#7A423D',
            bullMeterSlightlyBearishBg: '#884D47',
        },
        customText: {
            text1: '#707077',
            text2: '#F0F0F0',
            text3: '#B0B7C3',
            text4: '#8D8D91',
            text5: '#1a1b23',
            text6: '#FFF',
            text7: '#000000',
            text10: '#707077',
            text11: '#8D8D91',
            drawerTextColor: '#777E90',
            textDark: '#001C42',
        },

        // --- New design-system layer (Figma primitives + semantic) ---
        primitives: resolved,
        semantic,
    },
    typography: getTypographyOptions(DASHBOARD_FONT),
    breakpoints,
    components: getComponentOverrides(false),
};
